/**
 * 🔒 Region 9's rows, built once for every document that prints (decision
 * 0560): Sub Total, Discount where any was given, one row per tax at its own
 * rate, and the Total in bold. A reader comparing an Invoice with the Quote it
 * came from reads the same block in the same order on both sheets.
 *
 * 🔨 No projection writes a totals formatter of its own. A document hands over
 * figures in cents and the labels come from the one string lookup, so eighteen
 * document types cannot come to disagree about what a tax line reads as.
 */
import { formatCurrency } from '../money';
import { t } from '../strings';
import type { PrintFact } from '../documents/printRegions';
import type { PrintTotalsRowKey } from './content';

/** One tax as the sheet names it: the jurisdiction's label or the category's own name, the rate it charged and what it came to. */
export interface PrintTaxTotal {
  name: string;
  /** The rate as a percentage, so 4.7 is 4.70%; absent where the document stored no rate beside the amount. */
  ratePercent: number | null;
  amountCents: number;
  /** What the jurisdiction charged its rate on, where the document stored a row per jurisdiction (decision 0738); the sheet prints none of it (decision 1061). */
  baseCents?: number | null;
}

/** A tax as a document's own line carries it, before the rows of one rate are added together. */
export interface PrintTaxSource {
  name: string;
  ratePercent: number | null;
  amountCents: number | null;
}

/** The rate a tax row prints at: two decimals, the way a rate table publishes one. */
export function printTaxRateLabel(name: string, ratePercent: number | null): string {
  if (ratePercent === null || !Number.isFinite(ratePercent)) return name;
  return `${name} (${ratePercent.toFixed(2)}%)`;
}

/**
 * The lines' own tax rows added together, one row per name and rate, in the
 * order the document first met them. 🔨 A rate that raised nothing is dropped:
 * an exempt category on every line is not a tax line, it is the absence of one.
 */
export function printTaxTotals(sources: readonly PrintTaxSource[]): PrintTaxTotal[] {
  const out: PrintTaxTotal[] = [];
  for (const source of sources) {
    const amountCents = source.amountCents ?? 0;
    if (amountCents === 0) continue;
    const found = out.find((row) => row.name === source.name && row.ratePercent === source.ratePercent);
    if (found) found.amountCents += amountCents;
    else out.push({ name: source.name, ratePercent: source.ratePercent, amountCents });
  }
  return out;
}

/**
 * 🔒 Tax lines for a sheet whose document stored no jurisdiction (decision
 * 1001): a Purchase Order, a Bill, and a sales document saved before the rows
 * existed. Each is named *Tax* at its rate, never by the lines' category,
 * because a category is the kind of thing sold and never where the tax is owed.
 */
export function printUnplacedTaxTotals(sources: readonly Omit<PrintTaxSource, 'name'>[]): PrintTaxTotal[] {
  return printTaxTotals(sources.map((source) => ({ ...source, name: t('tax.preview.tax') })));
}

/**
 * 🔒 A document's stored rows as Region 9's tax lines, one per jurisdiction in
 * the order it draws them, each with the base it was charged on (decision
 * 0738). A document saved before the rows existed has none, and its lines'
 * own taxes stand in, one row per category and rate as before.
 */
export function printJurisdictionTaxTotals(rows: readonly { label: string; ratePercent: number; taxableBaseCents: number; taxCents: number }[]): PrintTaxTotal[] {
  return rows.filter((row) => row.taxCents !== 0).map((row) => ({ name: row.label, ratePercent: row.ratePercent, amountCents: row.taxCents, baseCents: row.taxableBaseCents }));
}

/**
 * What a document hands Region 9; everything is in cents and nothing is
 * formatted yet.
 *
 * 🔒 The block foots: `subtotalCents - discountCents + every tax = totalCents`
 * (decision 0560). The Sub Total is therefore the GROSS sum of the lines, before
 * the line discounts, and the Discount row is what those discounts took off. The
 * Amount column of the line table stays net, which is what a reader adds up when
 * they check one line rather than the sheet.
 *
 * 🔨 A caller passing the net sum as the subtotal and the discount beside it
 * subtracts the discount twice, and the sheet does not foot. Every projection
 * that carries a discount was doing exactly that until 2026-09-18.
 */
export interface PrintTotalsInput {
  currencyCode: string;
  /** The gross sum of the lines, BEFORE the line discounts. */
  subtotalCents: number;
  /** What the lines were discounted by, as a positive figure; nought prints no Discount row. */
  discountCents?: number;
  taxes?: readonly PrintTaxTotal[];
  /** What is owed: the sub total less the discount plus every tax. */
  totalCents: number;
  /** Rows a document carries after its Total, such as a Bill's paid and open figures. */
  trailing?: readonly { label: string; amountCents: number; key?: PrintTotalsRowKey }[];
}

/**
 * 🔒 Region 9's tax rows alone (decision 0738): one per jurisdiction, named
 * with its rate. 🔒 The sheet prints no base (T403; decision 1061, amending
 * 0738's sheet half): the product owner, 2026-09-27, *"we dont need the amount
 * listed"*, so the row reads *Pennsylvania Sales Tax (6.00%)* and its figure.
 *
 * A screen showing a sent revision's frozen rows passes `withBase`, because the
 * totals box on screen keeps its base and the copy is read beside it.
 */
export function printTaxRowFacts(taxes: readonly PrintTaxTotal[], currencyCode: string, options: { withBase?: boolean } = {}): PrintFact[] {
  const money = (amount: number) => formatCurrency({ amount, currencyCode });
  const withBase = (tax: PrintTaxTotal) => options.withBase === true && tax.baseCents !== null && tax.baseCents !== undefined;
  return taxes.map((tax) => ({ label: withBase(tax) ? `${printTaxRateLabel(tax.name, tax.ratePercent)} ${t('tax.preview.on')} ${money(tax.baseCents!)}` : printTaxRateLabel(tax.name, tax.ratePercent), value: money(tax.amountCents), key: 'tax' as const }));
}

/**
 * 🔒 The whole block, in the one order every Transactional sheet prints it.
 * The Total carries `strong`, which is what the renderer draws bold above a
 * rule; nothing else in the block does.
 */
export function printTotalsFacts(input: PrintTotalsInput): PrintFact[] {
  const money = (amount: number) => formatCurrency({ amount, currencyCode: input.currencyCode });
  const discountCents = input.discountCents ?? 0;
  return [
    // 🔒 Each row carries its key, so a template's own row can hide or retitle it (decision 0656).
    { label: t('document.print.subtotal'), value: money(input.subtotalCents), key: 'subtotal' },
    ...(discountCents !== 0 ? [{ label: t('document.print.discount'), value: money(discountCents), key: 'discount' as const }] : []),
    ...printTaxRowFacts(input.taxes ?? [], input.currencyCode),
    { label: t('document.print.total'), value: money(input.totalCents), strong: true, key: 'total' },
    ...(input.trailing ?? []).map((row) => ({ label: row.label, value: money(row.amountCents), ...(row.key ? { key: row.key } : {}) })),
  ];
}
